/**
 * Patient join requests (Quilt UGC service).
 *
 * The public side of "ask your clinic to join Quilt": the form reads the
 * clinic's name and condition choices, then submits the request. The clinic
 * approves or rejects it in the Quilt web app; on approval Quilt emails the
 * patient their invite code. No sign-in, no API key: these two routes are
 * public on the backend, and its CORS already allows *.quilt.gg.
 *
 * The backend keeps the feature behind PATIENT_JOIN_REQUESTS_ENABLED and
 * answers 404 while it is off, so callers get `unavailable` and can fall back.
 */

const API_BASE = (import.meta.env.VITE_UGC_API_URL || "https://medical.quilt.gg/api/ugc/v1").replace(/\/$/, "");

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface JoinFormCondition {
    key: string;
    label: string;
}

export interface JoinForm {
    clinic: { id: string; name: string };
    conditions: JoinFormCondition[];
}

export interface JoinRequestInput {
    clinicId: string;
    firstName: string;
    lastName: string;
    email: string;
    /** YYYY-MM-DD, or omitted */
    dob?: string;
    gender?: string;
    /** Keys from the form's condition list, e.g. ["opioids", "alcohol"] */
    conditions: string[];
}

export type JoinFormResult =
    | { status: "ok"; form: JoinForm }
    /** Feature off, clinic unknown or closed, or the service unreachable */
    | { status: "unavailable" };

export type SubmitResult = { success: true; message: string } | { success: false; error: string };

/**
 * The clinic a link points at: `?clinic=<uuid>` (the link Quilt emails), else
 * the page's configured clinic. Null when neither is a valid id.
 */
export function clinicIdFor(search: string, fallback?: string): string | null {
    const fromLink = new URLSearchParams(search).get("clinic");
    if (fromLink && UUID.test(fromLink)) return fromLink;
    return fallback && UUID.test(fallback) ? fallback : null;
}

export async function getJoinForm(clinicId: string): Promise<JoinFormResult> {
    try {
        const res = await fetch(`${API_BASE}/join-requests/form/${encodeURIComponent(clinicId)}`);
        if (!res.ok) return { status: "unavailable" };
        const body = await res.json();
        const data = body?.data;
        if (!data?.clinic?.id || !Array.isArray(data.conditions)) return { status: "unavailable" };
        return { status: "ok", form: data as JoinForm };
    } catch {
        return { status: "unavailable" };
    }
}

export async function submitJoinRequest(input: JoinRequestInput): Promise<SubmitResult> {
    try {
        const res = await fetch(`${API_BASE}/join-requests`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        const body = await res.json().catch(() => null);
        if (res.ok) {
            return { success: true, message: body?.message || "Thanks. Your clinic will review your request." };
        }
        if (res.status === 429) return { success: false, error: "Too many attempts. Please wait a few minutes and try again." };
        // 400s carry the problems; anything else is the service, not the patient
        if (res.status === 400 && body?.message) return { success: false, error: String(body.message) };
        return { success: false, error: "Something went wrong. Please try again." };
    } catch {
        return { success: false, error: "We couldn't reach Quilt. Check your connection and try again." };
    }
}
